import React, { useEffect, useState, useMemo } from "react";
import GroupedBarChart from "../../../../CHARTS/GroupedBarChart";
import { getCurrentFYByDate } from "../../../../Utilitie";

const PDDMonthYearGraph = ({ data, selectedFilter = "month" }) => {
  const key = ["total_trains_count", "total_total_time", "avg_avg_time"];
  const [chartData, setChartData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [checkBox, setCheckBox] = useState(false);
  const [keyName, setKeyName] = useState("avg_avg_time");

  // Convert hh:mm:ss to integer minutes
  const timeToMinutes = (timeStr) => {
    if (!timeStr || typeof timeStr !== "string") return 0;
    const [hours, minutes, seconds] = timeStr.split(":").map(Number);
    if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) return 0;
    return Math.floor(hours * 60 + minutes + seconds / 60);
  };

  const getDaysInPeriod = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  const transformData = (data, selectedFilter) => {
    if (!data || !Array.isArray(data)) {
      return {
        dimensions: ["product", "previous", "current"],
        series: [],
      };
    }

    const filterData = data.find((item) => item.filtered_by === selectedFilter);

    if (
      !filterData ||
      !filterData.current?.data ||
      !filterData.previous?.data
    ) {
      return {
        dimensions: ["product", "previous", "current"],
        series: [],
      };
    }

    const previousYear =
      getCurrentFYByDate(filterData.previous.end_date).FY || "Previous";
    const currentYear =
      getCurrentFYByDate(filterData.current.end_date).FY || "Current";
    const previousDays =
      filterData.previous.start_date && filterData.previous.end_date
        ? getDaysInPeriod(
            filterData.previous.start_date,
            filterData.previous.end_date,
          )
        : 30;
    const currentDays =
      filterData.current.start_date && filterData.current.end_date
        ? getDaysInPeriod(
            filterData.current.start_date,
            filterData.current.end_date,
          )
        : 30;

    const stationMap = new Map();

    // Process previous data
    filterData.previous.data.forEach((item) => {
      if (item.station_id && item.station_code && item[keyName] !== undefined) {
        let totalValue;
        if (keyName === "avg_avg_time") {
          totalValue = timeToMinutes(item[keyName]);
        } else if (keyName === "total_total_time") {
          totalValue = parseInt(item[keyName]) || 0;
        } else {
          // Handle total_trains_count explicitly
          totalValue = parseInt(item[keyName]) || 0;
        }
        const avgDailyValue =
          previousDays > 0 ? Math.round(totalValue / previousDays) : 0;
        stationMap.set(item.station_id, {
          product: item.station_code,
          [previousYear]: checkBox ? avgDailyValue : totalValue,
          [currentYear]: 0,
        });
      }
    });

    // Process current data
    filterData.current.data.forEach((item) => {
      if (item.station_id && item.station_code && item[keyName] !== undefined) {
        let totalValue;
        if (keyName === "avg_avg_time") {
          totalValue = timeToMinutes(item[keyName]);
        } else if (keyName === "total_total_time") {
          totalValue = parseInt(item[keyName]) || 0;
        } else {
          // Handle total_trains_count explicitly
          totalValue = parseInt(item[keyName]) || 0;
        }
        const avgDailyValue =
          currentDays > 0 ? Math.round(totalValue / currentDays) : 0;
        if (stationMap.has(item.station_id)) {
          stationMap.get(item.station_id)[currentYear] = checkBox
            ? avgDailyValue
            : totalValue;
        } else {
          stationMap.set(item.station_id, {
            product: item.station_code,
            [previousYear]: 0,
            [currentYear]: checkBox ? avgDailyValue : totalValue,
          });
        }
      }
    });

    // Sort series by currentYear values in descending order
    const series = Array.from(stationMap.values()).sort(
      (a, b) => b[currentYear] - a[currentYear],
    );

    return {
      dimensions: ["product", previousYear, currentYear],
      series,
    };
  };

  const computedChartData = useMemo(() => {
    if (!data) return null;
    return transformData(data, selectedFilter);
  }, [data, selectedFilter, keyName, checkBox]);

  useEffect(() => {
    setIsLoading(true);
    try {
      setChartData(computedChartData);
      setError(null);
    } catch (err) {
      setError("Failed to process chart data");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [computedChartData]);

  const formatButtonText = (str) => {
    return str
      .replace("total_total", "total")
      .replace("avg_avg", "avg")
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getTitle = () => {
    const suffix = checkBox ? "Daily Average" : "Total";
    if (keyName === "total_trains_count") {
      return `PDD Total Train Count (${suffix} in No)`;
    } else if (keyName === "total_total_time") {
      return `PDD Total Time (${suffix} in HH:MM)`;
    } else {
      return `PDD Average Time (${suffix} in HH:MM)`;
    }
  };

  if (error) {
    return (
      <div className="newUiBody text-danger">
        <p>Error: {error}</p>
        <button
          className="btn btn-sm btn-outline-primary"
          onClick={() => {
            setError(null);
            setIsLoading(true);
            try {
              const transformedData = transformData(data, selectedFilter);
              setChartData(transformedData);
            } catch (err) {
              setError("Failed to process chart data");
              console.error(err);
            } finally {
              setIsLoading(false);
            }
          }}
        >
          Retry
        </button>
      </div>
    );
  }
  return (
    <div className="newUiBody">
      <div className="row">
        <h6 className="p-1 bg-dark text-white">{getTitle()}</h6>
        {isLoading ? (
          <div>Loading chart...</div>
        ) : chartData && chartData.series.length > 0 ? (
          <GroupedBarChart
            data={chartData}
            from={`PDD_${keyName === "total_trains_count" ? "C" : "T"}`}
            chartType="bar"
          />
        ) : (
          <div>No data available for the selected filter.</div>
        )}
        <div className="d-flex">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              checked={checkBox}
              onChange={() => setCheckBox(!checkBox)}
              id="avgPerDayCheck"
            />
            <label className="form-check-label" htmlFor="avgPerDayCheck">
              Avg per Day
            </label>
          </div>
        </div>
        <hr />
        <div className="d-flex justify-content-between">
          {key.map((name, index) => (
            <button
              key={index}
              className={`btn btn-sm btn-outline-dark ${
                keyName === name ? "active" : ""
              }`}
              onClick={() => setKeyName(name)}
            >
              {formatButtonText(name)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PDDMonthYearGraph;
